import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

@customElement('info-accordion')
export class InfoAccordion extends LitElement {
  @property({ type: String }) title = 'Information';
  @property({ type: String }) storageKey?: string;

  @state() private isOpen = false;

  static styles = css`
    :host {
      display: block;
      border-radius: 8px;
      background: rgba(20, 33, 56, 0.95);
      backdrop-filter: blur(10px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
      overflow: hidden;
      border: 1px solid rgba(64, 249, 209, 0.2);
    }

    .accordion-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0.875rem 1rem;
      cursor: pointer;
      user-select: none;
      background: rgba(64, 249, 209, 0.1);
      transition: background-color 0.2s ease;
    }

    .accordion-header:hover {
      background: rgba(64, 249, 209, 0.15);
    }

    .accordion-title {
      font-weight: 600;
      font-size: 0.95rem;
      color: #40f9d1;
      margin: 0;
    }

    .accordion-icon {
      width: 20px;
      height: 20px;
      transition: transform 0.3s ease;
      fill: #40f9d1;
      flex-shrink: 0;
    }

    .accordion-icon.open {
      transform: rotate(180deg);
    }

    .accordion-content-wrapper {
      display: grid;
      grid-template-rows: 0fr;
      transition: grid-template-rows 0.3s ease;
    }

    .accordion-content-wrapper.open {
      grid-template-rows: 1fr;
    }

    .accordion-content {
      overflow: hidden;
    }

    .accordion-content-inner {
      padding: 1rem;
      color: #ffffff;
      font-size: 0.9rem;
      line-height: 1.5;
    }

    .accordion-content-inner h4 {
      color: #40f9d1;
      margin: 0 0 0.5rem 0;
      font-size: 0.95rem;
    }

    .accordion-content-inner ul {
      margin: 0 0 1rem 0;
      padding-left: 1.25rem;
      line-height: 1.6;
    }

    .accordion-content-inner ul:last-child {
      margin-bottom: 0;
    }

    .accordion-content-inner li {
      margin-bottom: 0.35rem;
    }

    .accordion-content-inner li:last-child {
      margin-bottom: 0;
    }

    .accordion-content-inner strong {
      color: #40f9d1;
      font-weight: 500;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    if (this.storageKey) {
      const stored = localStorage.getItem(this.storageKey);
      this.isOpen = stored === 'true';
    }
  }

  private toggleAccordion() {
    this.isOpen = !this.isOpen;
    if (this.storageKey) {
      localStorage.setItem(this.storageKey, String(this.isOpen));
    }
  }

  render() {
    return html`
      <div class="accordion-header" @click=${this.toggleAccordion}>
        <h3 class="accordion-title">${this.title}</h3>
        <svg
          class="accordion-icon ${this.isOpen ? 'open' : ''}"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
        >
          <path d="M7 10l5 5 5-5z" />
        </svg>
      </div>
      <div class="accordion-content-wrapper ${this.isOpen ? 'open' : ''}">
        <div class="accordion-content">
          <div class="accordion-content-inner">
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'info-accordion': InfoAccordion;
  }
}
